import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Copy, Download, Images, Link as LinkIcon, Loader2, Pencil, Plus, QrCode, RefreshCw, Save, Share2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  compressImageFile,
  getUploadErrorMessage,
  normalizeAndValidateExternalImage,
  validateImageFile,
} from "@/lib/adminImageUtils";
import {
  addAdminFolder,
  createAdminShareLink,
  createAdminEvent,
  deleteAdminEvent,
  deleteAdminFolder,
  deleteAdminFaceData,
  deactivateAdminShareLink,
  downloadAdminRegistrationsCsv,
  getAdminAccessRegistrations,
  getAdminEvents,
  getAdminPhotos,
  getAdminShareLinks,
  reprocessAdminFaceSearch,
  getAdminFaceIndexStatus,
  syncAdminEvent,
  startAdminFaceIndex,
  updateAdminShareLink,
  updateAdminEvent,
  updateAdminFolder,
  uploadAdminEventCover,
  verifyGalleryAdminPassword,
} from "@/lib/galleryApi";

const empty = { eventName: "", slug: "", googleDriveFolderId: "", pin: "", downloadEnabled: true, active: true };
const DEFAULT_SYNC_BATCH_SIZE = 8;
const AUTO_CONTINUE_DELAY_MS = 1400;
const SHARE_PRESETS = [
  { accessType: "guest", label: "Guest Access", description: "Registration-first guest link for viewing the selected private gallery." },
  { accessType: "own_photos", label: "Guest Access - Own Photos by Selfie", description: "Guests register and capture a guided selfie to see Strong and Possible matched photos." },
  { accessType: "full", label: "Full Access", description: "People with this link can see all event photos and folders." },
  { accessType: "partial", label: "Partial / Album Access", description: "People with this link can see only the selected albums/folders." },
  { accessType: "selection", label: "Photo Selection with Full Access", description: "Full access link with existing selection and ZIP tools enabled." },
];

export default function AdminGallery() {
  const [password, setPassword] = useState(() => sessionStorage.getItem("gallery-admin-password") || "");
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState("");
  const [syncProgress, setSyncProgress] = useState({});
  const syncProgressRef = useRef(syncProgress);
  const syncTimersRef = useRef(new Map());

  useEffect(() => {
    syncProgressRef.current = syncProgress;
  }, [syncProgress]);

  useEffect(() => () => {
    syncTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    syncTimersRef.current.clear();
  }, []);

  const load = async (value = password) => {
    const normalizedPassword = String(value || "").trim();
    setBusy("load");
    try {
      await verifyGalleryAdminPassword(normalizedPassword);
      const data = await getAdminEvents(normalizedPassword);
      setPassword(normalizedPassword);
      sessionStorage.setItem("gallery-admin-password", normalizedPassword);
      setEvents(data.events || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (password) load(password);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSyncState = (key, updater) => {
    setSyncProgress((current) => {
      const previous = current[key] || {};
      const next = typeof updater === "function" ? updater(previous) : updater;
      return { ...current, [key]: next };
    });
  };

  const clearScheduledSync = (key) => {
    const timer = syncTimersRef.current.get(key);
    if (timer) {
      window.clearTimeout(timer);
      syncTimersRef.current.delete(key);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy("create");
    try {
      await createAdminEvent(password, form);
      toast.success("Client gallery created");
      setForm(empty);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  const sync = async (eventRecord, folderId = "", options = {}) => {
    const key = folderId || eventRecord.id;
    const folder = folderId ? (eventRecord.folders || []).find((item) => item.id === folderId) : null;
    const folderName = folder?.folderPath || folder?.folderName || eventRecord.eventName;
    const previous = syncProgressRef.current[key] || {};
    const resume = options.resume === true;
    const initialize = options.initialize ?? !(resume && previous.initialized);
    const autoContinue = options.autoContinue ?? previous.autoContinue ?? true;

    clearScheduledSync(key);
    setBusy(key);
    updateSyncState(key, (current) => ({
      ...current,
      eventId: eventRecord.id,
      folderId: folderId || null,
      folderName,
      running: true,
      initialized: current.initialized || initialize,
      autoContinue,
      cancelled: false,
      error: "",
      status: initialize ? "starting" : "continuing",
      statusMessage: initialize
        ? `Scanning ${folderName} and preparing the first batch...`
        : `Resuming ${folderName} from the last safe batch...`,
    }));

    try {
      const result = await syncAdminEvent(password, eventRecord.id, {
        initialize,
        batchSize: DEFAULT_SYNC_BATCH_SIZE,
        folderId: folderId || undefined,
        resumeToken: previous.resumeToken || undefined,
        nextPageToken: previous.nextPageToken || undefined,
        batchNumber: Number(previous.batchNumber || 0) + 1,
      });
      const current = syncProgressRef.current[key] || {};
      const total = current.total || result.count || (current.synced || 0) + result.processed + result.remaining || result.remaining + result.processed;
      const synced = Math.max(0, total - result.remaining);
      const failed = Number(result.failedTotal ?? (Number(current.failed || 0) + Number(result.failed || 0)));
      const nextState = {
        ...current,
        eventId: eventRecord.id,
        folderId: folderId || null,
        folderName: result.folderName || folderName,
        resumeToken: result.resumeToken || "",
        nextPageToken: result.nextPageToken || "",
        batchNumber: result.batchNumber || Number(current.batchNumber || 0) + 1,
        total,
        synced,
        remaining: result.remaining,
        failed,
        folderCount: result.folderCount || current.folderCount || 0,
        running: false,
        initialized: true,
        resumable: Boolean(result.resumable),
        autoContinue,
        cancelled: false,
        status: result.complete ? "complete" : result.failed > 0 && result.processed === 0 ? "paused" : "ready",
        statusMessage: result.complete
          ? `Sync complete for ${result.folderName || folderName}.`
          : result.failed > 0 && result.processed === 0
            ? "Sync paused safely. Click Resume to continue."
            : autoContinue
              ? `Batch complete for ${result.folderName || folderName}. Continuing in ${Math.round(AUTO_CONTINUE_DELAY_MS / 1000)} seconds...`
              : "Batch complete. Click Resume to continue.",
      };
      updateSyncState(key, nextState);

      if (result.complete) {
        toast.success(`${total} photos synced and optimized`);
        await load();
        return;
      }

      if (result.failed > 0 && result.processed === 0) {
        toast.warning(`${result.folderName || folderName}: ${result.remaining} photos remaining. Click Resume to continue.`);
        await load();
        return;
      }

      if (autoContinue && !syncProgressRef.current[key]?.cancelled) {
        const timer = window.setTimeout(() => {
          syncTimersRef.current.delete(key);
          sync(eventRecord, folderId, {
            resume: true,
            autoContinue: syncProgressRef.current[key]?.autoContinue !== false,
          });
        }, AUTO_CONTINUE_DELAY_MS);
        syncTimersRef.current.set(key, timer);
      } else {
        toast.warning(`${result.folderName || folderName}: ${result.remaining} photos remaining. Click Resume to continue.`);
      }
      await load();
    } catch (error) {
      updateSyncState(key, (current) => ({
        ...current,
        eventId: eventRecord.id,
        folderId: folderId || null,
        folderName,
        running: false,
        resumable: true,
        autoContinue: false,
        cancelled: false,
        status: "error",
        error: error.message,
        statusMessage: error.message,
      }));
      toast.error(error.message);
      await load();
    } finally {
      setBusy("");
    }
  };

  const setAutoContinue = (key, autoContinue) => {
    clearScheduledSync(key);
    updateSyncState(key, (current) => ({
      ...current,
      autoContinue,
      cancelled: !autoContinue && current.running ? true : current.cancelled,
      statusMessage: !autoContinue && current.running
        ? "Auto continue stopped. Current batch will finish safely."
        : current.statusMessage,
    }));
  };

  if (!events.length && !sessionStorage.getItem("gallery-admin-password")) {
    return (
      <div className="p-10 max-w-xl">
        <div className="eyebrow text-[#C99A45]">Protected Tool</div>
        <h1 className="font-serif-display text-5xl mt-3">Client Gallery</h1>
        <p className="text-[#F7F1E8]/55 mt-5">Enter the server-side gallery admin password to manage private events.</p>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input mt-7 w-full" placeholder="Gallery admin password" />
        <button onClick={() => load(password)} disabled={!password || busy} className="btn-gold-fill mt-4">Unlock</button>
      </div>
    );
  }

  return (
    <div className="p-8 md:p-10">
      <div className="eyebrow text-[#C99A45]">Google Drive</div>
      <h1 className="font-serif-display text-5xl mt-3">Client Galleries</h1>
      <div className="grid xl:grid-cols-[390px_1fr] gap-8 mt-10">
        <form onSubmit={submit} className="border border-[#C99A45]/20 p-6 space-y-4 self-start">
          <h2 className="font-serif-display text-3xl flex gap-3 items-center"><Plus size={18} className="text-[#C99A45]" /> Add Event</h2>
          {[
            ["eventName", "Event name"],
            ["slug", "Slug (example: patel-wedding)"],
            ["googleDriveFolderId", "Google Drive folder ID"],
            ["pin", "Client PIN"],
          ].map(([key, label]) => (
            <input key={key} required value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="admin-input w-full" placeholder={label} />
          ))}
          <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.downloadEnabled} onChange={(e) => setForm({ ...form, downloadEnabled: e.target.checked })} /> Allow downloads</label>
          <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
          <button disabled={busy === "create"} className="btn-gold-fill w-full">{busy === "create" ? "Creating..." : "Create Gallery"}</button>
        </form>
        <div className="space-y-5">
          {events.map((event) => (
            <EventManager
              key={event.id}
              event={event}
              password={password}
              busy={busy}
              progress={syncProgress}
              onBusy={setBusy}
              onReload={load}
              onSync={sync}
              onAutoContinueChange={setAutoContinue}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function EventManager({ event, password, busy, progress, onBusy, onReload, onSync, onAutoContinueChange }) {
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [folderForm, setFolderForm] = useState({ folderName: "", driveFolderId: "" });
  const [values, setValues] = useState({
    eventName: event.eventName,
    slug: event.slug,
    googleDriveFolderId: event.googleDriveFolderId || "",
    pin: "",
    downloadEnabled: event.downloadEnabled,
    active: event.active,
    coverPhotoId: event.coverPhotoId || "",
    coverImageUrl: event.coverImageUrl || "",
  });
  const eventProgress = progress[event.id];

  useEffect(() => {
    if (!editing) return;
    getAdminPhotos(password, event.id).then((data) => setPhotos(data.photos || [])).catch(() => setPhotos([]));
  }, [editing, event.id, password]);

  const save = async () => {
    onBusy(`edit:${event.id}`);
    try {
      await updateAdminEvent(password, event.id, values);
      toast.success("Gallery event updated");
      setEditing(false);
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  const removeEvent = async () => {
    if (!window.confirm(`Delete "${event.eventName}" and its gallery metadata? Drive files will not be deleted.`)) return;
    onBusy(`delete:${event.id}`);
    try {
      await deleteAdminEvent(password, event.id);
      toast.success("Gallery event deleted");
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  const addFolder = async (e) => {
    e.preventDefault();
    onBusy(`folder:${event.id}`);
    try {
      await addAdminFolder(password, event.id, folderForm);
      setFolderForm({ folderName: "", driveFolderId: "" });
      toast.success("Drive folder added");
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  const removeFolder = async (folder) => {
    if (!window.confirm(`Remove folder "${folder.folderName}" from this gallery?`)) return;
    onBusy(folder.id);
    try {
      await deleteAdminFolder(password, event.id, folder.id);
      toast.success("Folder removed");
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  const moveFolder = async (folder, direction) => {
    onBusy(folder.id);
    try {
      await updateAdminFolder(password, event.id, folder.id, {
        displayOrder: Math.max(0, Number(folder.displayOrder || 0) + direction),
      });
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  const setFolderCover = async (folder, coverPhotoId) => {
    onBusy(folder.id);
    try {
      await updateAdminFolder(password, event.id, folder.id, { coverPhotoId });
      toast.success("Album cover updated");
      await onReload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      onBusy("");
    }
  };

  return (
    <div className="border border-[#C99A45]/20 p-6">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
        <div>
          <Images size={18} className="text-[#C99A45] mb-3" />
          <h2 className="font-serif-display text-3xl">{event.eventName}</h2>
          <div className="text-xs text-[#F7F1E8]/45 mt-2">/gallery/{event.slug}</div>
          {eventProgress && (
            <SyncStatusCard
              progress={eventProgress}
              onResume={() => onSync(event, "", { resume: true })}
              onAutoContinueChange={onAutoContinueChange}
            />
          )}
          {event.syncedAt && <div className="text-[10px] text-white/35 mt-2">Last synced {new Date(event.syncedAt).toLocaleString()}</div>}
          <div className="eyebrow text-[9px] mt-3">{event.active ? "Active" : "Inactive"} - {event.downloadEnabled ? "Downloads on" : "Downloads off"}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditing((value) => !value)} className="btn-gold-outline flex items-center gap-2">
            {editing ? <X size={14} /> : <Pencil size={14} />} {editing ? "Close" : "Edit"}
          </button>
          <button onClick={() => setSharing((value) => !value)} className="btn-gold-outline flex items-center gap-2">
            {sharing ? <X size={14} /> : <Share2 size={14} />} {sharing ? "Close share" : "Share"}
          </button>
          <button onClick={() => onSync(event, "", { resume: Boolean(eventProgress?.remaining) })} disabled={busy === event.id} className="btn-gold-outline flex items-center gap-2">
            <RefreshCw size={14} className={busy === event.id ? "animate-spin" : ""} /> {busy === event.id ? "Syncing..." : eventProgress?.remaining ? "Resume sync" : "Sync all"}
          </button>
          <button onClick={removeEvent} disabled={busy === `delete:${event.id}`} className="border border-red-500/35 text-red-300 px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-7 pt-7 border-t border-[#C99A45]/15 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <input className="admin-input" value={values.eventName} onChange={(e) => setValues({ ...values, eventName: e.target.value })} placeholder="Event name" />
            <input className="admin-input" value={values.slug} onChange={(e) => setValues({ ...values, slug: e.target.value })} placeholder="Slug" />
            <input className="admin-input" value={values.googleDriveFolderId} onChange={(e) => setValues({ ...values, googleDriveFolderId: e.target.value })} placeholder="Primary Drive folder ID" />
            <input className="admin-input" type="password" value={values.pin} onChange={(e) => setValues({ ...values, pin: e.target.value })} placeholder="New PIN (leave blank to keep)" />
          </div>
          <select className="admin-input w-full" value={values.coverPhotoId} onChange={(e) => setValues({ ...values, coverPhotoId: e.target.value })}>
            <option value="">Automatic first-photo cover</option>
            {photos.map((photo) => <option key={photo.id} value={photo.id}>{photo.name}</option>)}
          </select>
          <GalleryCoverPicker
            eventId={event.id}
            password={password}
            value={values.coverImageUrl}
            onChange={(coverImageUrl) => setValues({ ...values, coverImageUrl })}
            label="Hero image upload (manual gallery cover - overrides selected cover photo)"
          />
          <div className="flex flex-wrap gap-5 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={values.downloadEnabled} onChange={(e) => setValues({ ...values, downloadEnabled: e.target.checked })} /> Allow downloads</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={values.active} onChange={(e) => setValues({ ...values, active: e.target.checked })} /> Active</label>
          </div>
          <button onClick={save} disabled={busy === `edit:${event.id}`} className="btn-gold-fill flex items-center gap-2"><Save size={14} /> Save event</button>

          <div className="pt-5">
            <div className="eyebrow text-[#C99A45] mb-3">Drive folders and albums</div>
            <div className="space-y-2">
              {(event.folders || []).map((folder) => (
                <div key={folder.id} className="border border-white/10 px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{folder.folderPath || folder.folderName}</div>
                    <div className="text-[10px] text-white/35 truncate">{folder.driveFolderId}</div>
                    {progress[folder.id] && (
                      <div className="mt-3 max-w-xl">
                        <SyncStatusCard
                          compact
                          progress={progress[folder.id]}
                          onResume={() => onSync(event, folder.id, { resume: true })}
                          onAutoContinueChange={onAutoContinueChange}
                        />
                      </div>
                    )}
                    {!folder.isRoot && (
                      <select className="admin-input mt-2 max-w-sm" value={folder.coverPhotoId || ""}
                        onChange={(e) => setFolderCover(folder, e.target.value)}>
                        <option value="">Automatic first-photo cover</option>
                        {photos.filter((photo) => photo.folderId === folder.id).map((photo) => (
                          <option key={photo.id} value={photo.id}>{photo.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => moveFolder(folder, -1)} className="p-2 border border-white/10" aria-label="Move folder up"><ChevronUp size={13} /></button>
                    <button onClick={() => moveFolder(folder, 1)} className="p-2 border border-white/10" aria-label="Move folder down"><ChevronDown size={13} /></button>
                    {folder.isRoot && (
                      <button onClick={() => onSync(event, folder.id, { resume: Boolean(progress[folder.id]?.remaining) })} disabled={busy === folder.id} className="px-3 py-2 border border-[#C99A45]/25 text-xs">
                        {busy === folder.id ? "Syncing..." : progress[folder.id]?.remaining ? "Resume sync" : "Sync folder"}
                      </button>
                    )}
                    <button onClick={() => removeFolder(folder)} className="p-2 border border-red-500/20 text-red-300" aria-label="Remove folder"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={addFolder} className="grid md:grid-cols-[1fr_1.5fr_auto] gap-2 mt-3">
              <input required className="admin-input" value={folderForm.folderName} onChange={(e) => setFolderForm({ ...folderForm, folderName: e.target.value })} placeholder="Album/root name" />
              <input required className="admin-input" value={folderForm.driveFolderId} onChange={(e) => setFolderForm({ ...folderForm, driveFolderId: e.target.value })} placeholder="Google Drive folder ID" />
              <button disabled={busy === `folder:${event.id}`} className="btn-gold-outline">Add folder</button>
            </form>
          </div>
        </div>
      )}
      {sharing && (
        <ShareLinksPanel
          event={event}
          password={password}
        />
      )}
    </div>
  );
}

function ShareLinksPanel({ event, password }) {
  const [shareLinks, setShareLinks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [faceJob, setFaceJob] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setBusy("load-share");
    try {
      const [linksData, registrationsData] = await Promise.all([
        getAdminShareLinks(password, event.id),
        getAdminAccessRegistrations(password, event.id),
      ]);
      setShareLinks(linksData.shareLinks || []);
      setAlbums(linksData.albums || []);
      setRegistrations(registrationsData.registrations || []);
      getAdminFaceIndexStatus(password, event.id).then((result) => setFaceJob(result.job || null)).catch(() => setFaceJob(null));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    load();
  }, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPreset = async (preset) => {
    setBusy(`create:${preset.accessType}`);
    try {
      await createAdminShareLink(password, event.id, {
        accessType: preset.accessType,
        pinCode: generatePin(),
        requirePin: true,
        requireRegistration: true,
        allowedAlbumIds: preset.accessType === "partial" ? albums.slice(0, 1).map((album) => album.id) : [],
        faceSearchSettings: preset.accessType === "own_photos" ? {
          enabled: true,
          requireLiveSelfie: true,
          showPossibleMatches: true,
          allowPossibleDownloads: false,
          strongThreshold: Number(import.meta.env.VITE_FACE_MATCH_STRONG_THRESHOLD || 0.82),
          possibleThreshold: Number(import.meta.env.VITE_FACE_MATCH_POSSIBLE_THRESHOLD || 0.62),
        } : {},
      });
      toast.success(`${preset.label} link created`);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  const saveLink = async (link, updates) => {
    setBusy(link.id);
    try {
      await updateAdminShareLink(password, link.id, { ...link, ...updates });
      toast.success("Share link updated");
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  const deactivate = async (link) => {
    if (!window.confirm("Deactivate this share link?")) return;
    setBusy(`deactivate:${link.id}`);
    try {
      await deactivateAdminShareLink(password, link.id);
      toast.success("Share link deactivated");
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  const exportCsv = async () => {
    setBusy("csv");
    try {
      await downloadAdminRegistrationsCsv(password, event.id, `${event.slug || event.eventName}-registrations.csv`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy("");
    }
  };

  const startFaceIndex = async () => {
    setBusy("face-index");
    try {
      await startAdminFaceIndex(password, event.id);
      toast.success("Face indexing started");
    } catch (error) {
      toast.error(error.message);
    } finally {
      await load();
      setBusy("");
    }
  };

  const reprocessSelfie = async (registration) => {
    setBusy(`reprocess:${registration.id}`);
    try {
      await reprocessAdminFaceSearch(password, registration.id);
      toast.success("Selfie matches reprocessed");
    } catch (error) {
      toast.error(error.message);
    } finally {
      await load();
      setBusy("");
    }
  };

  const deleteFaceData = async (registration) => {
    if (!window.confirm(`Delete face data for ${registration.name || "this registration"}?`)) return;
    setBusy(`delete-face:${registration.id}`);
    try {
      await deleteAdminFaceData(password, registration.id);
      toast.success("Face data deleted");
    } catch (error) {
      toast.error(error.message);
    } finally {
      await load();
      setBusy("");
    }
  };

  return (
    <div className="mt-7 pt-7 border-t border-[#C99A45]/15">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow text-[#C99A45]">Fotoowl-style sharing</div>
          <h3 className="font-serif-display text-3xl mt-2">Share Links & Access Tracking</h3>
          <p className="text-sm text-[#F7F1E8]/55 mt-2 max-w-2xl">
            Create one client-ready link per access type. Registration and access logs stay server-side in Supabase.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={startFaceIndex} disabled={busy === "face-index"} className="btn-gold-outline flex items-center gap-2">
            <RefreshCw size={14} /> Index faces for this event
          </button>
          <button onClick={exportCsv} disabled={busy === "csv"} className="btn-gold-outline flex items-center gap-2">
            <Download size={14} /> Download Registrations CSV
          </button>
        </div>
      </div>
      {faceJob && (
        <div className="mt-4 border border-[#C99A45]/15 p-4 text-sm text-[#F7F1E8]/60">
          <span className="text-[#C99A45] uppercase tracking-[0.18em] text-[10px]">Face index status</span>
          <div className="mt-2">
            {faceJob.status} - processed {faceJob.processedPhotos || 0} / {faceJob.totalPhotos || 0} photos,
            faces found {faceJob.totalFaces || 0}, failed {faceJob.failedPhotos || 0}
          </div>
          {faceJob.errorMessage && <div className="text-red-200 mt-1">{faceJob.errorMessage}</div>}
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-4 mt-6">
        {SHARE_PRESETS.map((preset) => {
          const existing = shareLinks.filter((link) => link.accessType === preset.accessType && link.isActive);
          return (
            <div key={preset.accessType} className="border border-[#C99A45]/20 p-5 bg-black/25">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-serif-display text-2xl">{preset.label}</h4>
                  <p className="text-xs text-[#F7F1E8]/50 mt-1">{preset.description}</p>
                </div>
                <button onClick={() => createPreset(preset)} disabled={busy === `create:${preset.accessType}`} className="btn-gold-outline text-[11px] px-3 py-2">
                  <Plus size={12} /> New
                </button>
              </div>

              <div className="space-y-4 mt-5">
                {existing.length ? existing.map((link) => (
                  <ShareLinkCard
                    key={link.id}
                    event={event}
                    link={link}
                    albums={albums}
                    busy={busy === link.id || busy === `deactivate:${link.id}`}
                    onSave={saveLink}
                    onDeactivate={deactivate}
                  />
                )) : (
                  <div className="border border-white/10 p-4 text-xs text-[#F7F1E8]/45">No active {preset.label.toLowerCase()} link yet.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-7 border border-[#C99A45]/15">
        <div className="p-4 border-b border-[#C99A45]/15 flex items-center justify-between gap-3">
          <div>
            <div className="eyebrow text-[9px] text-[#C99A45]">Access registrations</div>
            <div className="text-sm text-[#F7F1E8]/60">{registrations.length} registered visitor{registrations.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={load} className="btn-gold-outline text-[11px] px-3 py-2"><RefreshCw size={12} /> Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[#C99A45] uppercase tracking-[0.18em] text-[10px]">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Mobile</th>
                <th className="p-3">Link type</th>
                <th className="p-3">Selfie</th>
                <th className="p-3">Matches</th>
                <th className="p-3">Consent</th>
                <th className="p-3">Registered at</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {registrations.map((item) => (
                <tr key={item.id} className="text-[#F7F1E8]/70">
                  <td className="p-3">{item.name}</td>
                  <td className="p-3">{item.email || "-"}</td>
                  <td className="p-3">{item.mobile || "-"}</td>
                  <td className="p-3">{item.accessType || "-"}</td>
                  <td className="p-3">
                    {item.selfie?.selfieStatus || "-"}
                    {item.selfie && <div className="text-[10px] text-white/35">{item.selfie.capturedFrameCount || 0} captured / {item.selfie.validFaceFrameCount || 0} valid</div>}
                  </td>
                  <td className="p-3">{item.strongCount || 0} strong / {item.possibleCount || 0} possible</td>
                  <td className="p-3">{item.consentPrivacy ? "Privacy" : "-"}{item.consentMarketing ? " + Marketing" : ""}</td>
                  <td className="p-3">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => reprocessSelfie(item)} className="btn-gold-outline text-[10px] px-2 py-1">Reprocess</button>
                      <button onClick={() => deleteFaceData(item)} className="border border-red-500/30 text-red-300 text-[10px] px-2 py-1 uppercase tracking-widest">Delete face data</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!registrations.length && (
                <tr><td className="p-4 text-[#F7F1E8]/45" colSpan={9}>No registrations yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ShareLinkCard({ event, link, albums, busy, onSave, onDeactivate }) {
  const [draft, setDraft] = useState(link);
  const shareUrl = `${window.location.origin}/gallery/share/${link.token}`;
  const showAlbumPicker = ["partial", "guest"].includes(draft.accessType);

  useEffect(() => setDraft(link), [link]);

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const message = `Hi, your private Mangalfera Films gallery is ready.\nOpen this link: ${shareUrl}${draft.requirePin && draft.pinCode ? `\nPIN: ${draft.pinCode}` : ""}`;

  const toggleAlbum = (albumId) => {
    const current = new Set(draft.allowedAlbumIds || []);
    if (current.has(albumId)) current.delete(albumId);
    else current.add(albumId);
    setDraft({ ...draft, allowedAlbumIds: [...current] });
  };

  return (
    <div className="border border-white/10 p-4 space-y-3">
      <div className="grid md:grid-cols-[1fr_120px] gap-2">
        <input readOnly className="admin-input w-full text-xs" value={shareUrl} />
        <input className="admin-input w-full text-xs" value={draft.pinCode || ""} onChange={(e) => setDraft({ ...draft, pinCode: e.target.value })} placeholder="PIN" />
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-[#F7F1E8]/65">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!draft.requirePin} onChange={(e) => setDraft({ ...draft, requirePin: !e.target.checked })} />
          Do not ask PIN
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={draft.requireRegistration} onChange={(e) => setDraft({ ...draft, requireRegistration: e.target.checked })} />
          Require registration
        </label>
      </div>

      {showAlbumPicker && (
        <div className="border border-[#C99A45]/10 p-3">
          <div className="eyebrow text-[9px] text-[#C99A45] mb-2">Allowed albums</div>
          <div className="grid sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {albums.map((album) => (
              <label key={album.id} className="flex items-center gap-2 text-xs text-[#F7F1E8]/70">
                <input type="checkbox" checked={(draft.allowedAlbumIds || []).includes(album.id)} onChange={() => toggleAlbum(album.id)} />
                <span>{album.folderPath || album.folderName} <span className="text-white/35">({album.photoCount || 0})</span></span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-[#F7F1E8]/35 mt-2">If no album is selected, this link can see all albums for its event.</p>
        </div>
      )}

      {draft.accessType === "own_photos" && (
        <div className="border border-[#C99A45]/10 p-3 space-y-3">
          <div className="eyebrow text-[9px] text-[#C99A45]">Face search settings</div>
          <div className="grid md:grid-cols-2 gap-3 text-xs text-[#F7F1E8]/65">
            {[
              ["enabled", "Enable Face Search / Own Photos"],
              ["requireLiveSelfie", "Require Live Selfie Capture"],
              ["showPossibleMatches", "Show Possible Matches"],
              ["allowPossibleDownloads", "Allow Possible Matches Download"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.faceSearchSettings?.[key] !== false}
                  onChange={(event) => setDraft({
                    ...draft,
                    faceSearchSettings: { ...(draft.faceSearchSettings || {}), [key]: event.target.checked },
                  })}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <input
              className="admin-input text-xs"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={draft.faceSearchSettings?.strongThreshold ?? 0.82}
              onChange={(event) => setDraft({
                ...draft,
                faceSearchSettings: { ...(draft.faceSearchSettings || {}), strongThreshold: Number(event.target.value) },
              })}
              placeholder="Strong threshold"
            />
            <input
              className="admin-input text-xs"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={draft.faceSearchSettings?.possibleThreshold ?? 0.62}
              onChange={(event) => setDraft({
                ...draft,
                faceSearchSettings: { ...(draft.faceSearchSettings || {}), possibleThreshold: Number(event.target.value) },
              })}
              placeholder="Possible threshold"
            />
          </div>
          <p className="text-[10px] text-[#F7F1E8]/35">
            Similarity-score engines should treat higher scores as better: strong if score >= strong threshold, possible if score >= possible threshold.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => copy(shareUrl, "Link")} className="btn-gold-outline text-[11px] px-3 py-2"><Copy size={12} /> Copy link</button>
        <button onClick={() => copy(message, "Message")} className="btn-gold-outline text-[11px] px-3 py-2"><Copy size={12} /> Copy with message</button>
        <button disabled className="border border-white/10 text-white/30 px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-2"><QrCode size={12} /> QR coming soon</button>
        <button onClick={() => onSave(link, draft)} disabled={busy} className="btn-gold-fill text-[11px] px-3 py-2"><Save size={12} /> Save</button>
        <button onClick={() => onDeactivate(link)} disabled={busy} className="border border-red-500/30 text-red-300 px-3 py-2 text-[11px] uppercase tracking-widest"><Trash2 size={12} /> Deactivate</button>
      </div>
    </div>
  );
}

function generatePin() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function SyncStatusCard({ progress, onResume, onAutoContinueChange, compact = false }) {
  const syncKey = progress.folderId || progress.eventId;
  const synced = Math.max(0, Number(progress.synced || 0));
  const total = Math.max(synced, Number(progress.total || 0));
  const remaining = Math.max(0, Number(progress.remaining || 0));
  const failed = Math.max(0, Number(progress.failed || 0));

  return (
    <div className={`border border-[#C99A45]/18 bg-black/30 ${compact ? "p-3" : "mt-4 p-4"}`}>
      <div className="eyebrow text-[9px] text-[#C99A45]">
        {progress.folderName || "Current folder"}
        {progress.folderCount ? ` - ${progress.folderCount} folders` : ""}
      </div>
      <div className="text-sm text-[#F7F1E8] mt-2">Synced {synced} / {total || "?"} photos</div>
      <div className="text-xs text-[#F7F1E8]/65 mt-1">Remaining {remaining}{failed ? ` - ${failed} failed` : ""}</div>
      <div className="text-xs text-[#F7F1E8]/55 mt-2">{progress.statusMessage}</div>
      <div className={`flex flex-wrap items-center gap-3 ${compact ? "mt-3" : "mt-4"}`}>
        <label className="flex items-center gap-2 text-[11px] text-[#F7F1E8]/75">
          <input
            type="checkbox"
            checked={progress.autoContinue !== false}
            onChange={(event) => onAutoContinueChange(syncKey, event.target.checked)}
          />
          Auto continue sync
        </label>
        {remaining > 0 && !progress.running && (
          <button onClick={onResume} className="btn-gold-outline text-[11px] px-3 py-2">Resume sync</button>
        )}
        {progress.running && (
          <button onClick={() => onAutoContinueChange(syncKey, false)} className="btn-gold-outline text-[11px] px-3 py-2">
            Pause after batch
          </button>
        )}
      </div>
    </div>
  );
}

function GalleryCoverPicker({ eventId, password, value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(false);
  const [urlVal, setUrlVal] = useState("");
  const [localPreview, setLocalPreview] = useState("");
  const [error, setError] = useState("");

  const coverSrc = localPreview || value;

  const uploadFile = async (file) => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(objectUrl);
    setUploading(true);
    setError("");
    try {
      validateImageFile(file);
      const optimized = await compressImageFile(file, { maxEdge: 3000, quality: 0.86 });
      const data = await readFileAsBase64(optimized);
      const result = await uploadAdminEventCover(password, eventId, {
        filename: optimized.name,
        contentType: optimized.type,
        data,
      });
      onChange(result.url);
      toast.success("Cover image uploaded. Click Save event to persist it.");
    } catch (error) {
      const message = getUploadErrorMessage(error);
      console.error("[GALLERY_COVER_UPLOAD_ERROR]", {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        message,
        error,
      });
      setError(message);
      toast.error(message);
      setLocalPreview("");
    } finally {
      setUploading(false);
      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        setLocalPreview("");
      }, 1200);
    }
  };

  const applyUrl = async () => {
    if (!urlVal.trim()) return;
    setUploading(true);
    setError("");
    try {
      const imageUrl = await normalizeAndValidateExternalImage(urlVal);
      onChange(imageUrl);
      setUrlVal("");
      setUrlInput(false);
      toast.success("Cover URL set");
    } catch (err) {
      const message = err?.message || "Could not load this image URL.";
      console.error("[GALLERY_COVER_URL_ERROR]", { input: urlVal, message });
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="eyebrow text-[10px] text-[#C99A45]/80 block mb-2">{label}</label>
      {coverSrc && (
        <div className="relative w-full max-w-sm mb-3 border border-[#C99A45]/20 bg-black">
          <img src={coverSrc} alt="Gallery cover preview" className="w-full aspect-[16/9] object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-black/70 p-1.5 text-white hover:bg-red-600 transition"
            aria-label="Remove cover image"
          >
            <X size={13} />
          </button>
        </div>
      )}
      <label
        className="block border border-dashed border-[#C99A45]/30 p-6 text-center cursor-pointer hover:border-[#C99A45]/70 transition"
        onDrop={(event) => {
          event.preventDefault();
          uploadFile(event.dataTransfer.files?.[0]);
        }}
        onDragOver={(event) => event.preventDefault()}
      >
        {uploading ? (
          <>
            <Loader2 size={20} className="mx-auto text-[#C99A45] animate-spin mb-2" />
            <p className="text-xs text-[#F7F1E8]/50">Uploading cover image...</p>
          </>
        ) : (
          <>
            <Upload size={20} className="mx-auto text-[#C99A45]/50 mb-2" />
            <p className="text-xs text-[#F7F1E8]/50">Drop or click to upload manual hero image</p>
            <p className="text-[10px] text-[#C99A45]/40 mt-1">JPG, PNG, WebP - optimized before secure upload</p>
          </>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={uploading}
          className="hidden"
          onChange={(event) => uploadFile(event.target.files?.[0])}
        />
      </label>
      <button
        type="button"
        className="mt-2 flex items-center gap-1 text-[10px] text-[#C99A45]/50 hover:text-[#C99A45] transition"
        onClick={() => setUrlInput((current) => !current)}
      >
        <LinkIcon size={10} /> Paste URL instead
      </button>
      {urlInput && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={urlVal}
            onChange={(event) => setUrlVal(event.target.value)}
            placeholder="https://... or Google Drive file link"
            className="flex-1 bg-transparent border-b border-[#C99A45]/30 focus:border-[#C99A45] outline-none py-1 text-xs text-[#F7F1E8]"
          />
          <button
            type="button"
            onClick={applyUrl}
            disabled={uploading}
            className="text-[10px] eyebrow text-[#C99A45] border border-[#C99A45]/40 px-3 py-1"
          >
            {uploading ? "Checking" : "Use"}
          </button>
        </div>
      )}
      {error && (
        <div className="mt-2 border border-red-500/30 bg-red-950/20 px-3 py-2 text-xs text-red-100">
          {error}
        </div>
      )}
    </div>
  );
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = () => reject(new Error("Could not read selected cover image"));
    reader.readAsDataURL(file);
  });
}
